import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  makeStyles,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Select,
  CircularProgress,
} from "@material-ui/core";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { 
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Star as StarIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon
} from "@material-ui/icons";

import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import { i18n } from "../../translate/i18n";
import usePlans from "../../hooks/usePlans";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
    maxWidth: "100%",
    margin: "0 auto",
  },
  // ✅ CORREGIDO: FormCard dinámico según tema
  formCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === 'dark' ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  formCardContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  formTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    textAlign: "center",
    justifyContent: "center",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  logo: {
    height: "60px",
    width: "auto",
    maxWidth: "200px",
  },
  formGrid: {
    marginTop: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#fafafa",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  // ✅ CORREGIDO: SelectField dinámico según tema
  selectField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
      },
    },
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "center",
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
  },
  actionButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: "600",
    padding: theme.spacing(1.5, 4),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
    },
    transition: "all 0.2s ease",
    minWidth: 140,
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  planDescription: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  successMessage: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    border: "1px solid #c3e6cb",
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    border: "1px solid #f5c6cb",
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nombre muy corto")
    .max(50, "Nombre muy largo")
    .required("Nombre requerido"),
  password: Yup.string()
    .min(5, "Contraseña muy corta")
    .max(50, "Contraseña muy larga")
    .required("Contraseña requerida"),
  email: Yup.string()
    .email("Email inválido")
    .required("Email requerido"),
  phone: Yup.string()
    .min(10, "Número de teléfono muy corto")
    .required("Número de WhatsApp requerido"),
  planId: Yup.string()
    .required("Plan requerido"),
});

const RegisterCompanyImproved = ({ onSuccess, onCancel }) => {
  const classes = useStyles();
  const history = useHistory();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const countdownIntervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const initialState = { 
    name: "", 
    email: "", 
    phone: "", 
    password: "", 
    planId: "" 
  };

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const list = await listPlans();
        setPlans(list);
      } catch (error) {
        console.error("Error cargando planes:", error);
        toast.error("Error al cargar los planes disponibles");
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpiar intervalos y timers al desmontar el componente
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleSignUp = async (values) => {
    setLoading(true);
    try {
      // Configurar valores por defecto
      const dueDate = moment().add(3, "day").format();
      const companyData = {
        ...values,
        recurrence: "MENSAL",
        dueDate: dueDate,
        status: true,
        campaignsEnabled: true,
      };

      const response = await openApi.post("/companies/cadastro", companyData);
      const createdCompany = response.data;
      
      // ✅ MEJORADO: Mostrar mensaje de éxito siempre, incluso con callback
      toast.success(i18n.t("signup.toasts.success") || "¡Empresa registrada exitosamente!");
      
      // Limpiar cualquier timer anterior si existe
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
      
      // ✅ MEJORADO: Mostrar mensaje de éxito visualmente
      setRegistrationSuccess(true);
      setCountdown(2);
      
      // Iniciar countdown para cerrar/redirigir
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return newCount;
        });
      }, 1000);
      
      // ✅ MEJORADO: Si hay onSuccess callback, esperar 2 segundos para mostrar éxito, luego cerrar
      if (onSuccess) {
        // Esperar 2 segundos para que el usuario vea el mensaje de éxito
        redirectTimeoutRef.current = setTimeout(() => {
        // Limpiar timers
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setRegistrationSuccess(false);
          setCountdown(2);
          // Llamar al callback con la empresa creada (incluye ID)
          onSuccess(createdCompany || companyData);
        }, 2000);
      } else {
        // Guardar referencia del timeout para redirección (comportamiento original)
        redirectTimeoutRef.current = setTimeout(() => {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setRegistrationSuccess(false);
          setCountdown(2);
          // Forzar redirección
          window.location.href = "/settings";
        }, 2000);
      }
      
    } catch (err) {
      console.error("Error registrando empresa:", err);
      // ✅ MEJORADO: Mostrar error más claro
      toastError(err);
      // ✅ Asegurar que el estado de éxito esté desactivado
      setRegistrationSuccess(false);
      setCountdown(2);
      // ✅ El formulario permanece habilitado para que el usuario pueda corregir y reintentar
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  const logo = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logotipos/login.png`;


  return (
    <div className={classes.root}>
      <Card className={classes.formCard}>
        <CardContent className={classes.formCardContent}>
          {/* Logo */}
          <div className={classes.logoContainer}>
            <img src={logo} alt="Watoolx" className={classes.logo} />
          </div>
          
          {/* Título */}
          <Typography variant="h5" className={classes.formTitle}>
            <PersonAddIcon />
            Registrar Nueva Empresa
          </Typography>
          
          {/* Mensaje de éxito con contador */}
          {registrationSuccess && (
            <div className={classes.successMessage}>
              <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 8 }}>
                <span role="img" aria-label="Exito">✅</span> ¡Empresa Registrada Exitosamente!
              </Typography>
              <Typography variant="body2">
                {onSuccess 
                  ? `Cerrando el formulario en ${countdown} segundo${countdown !== 1 ? 's' : ''}...`
                  : `Redirigiendo a la pestaña de Empresas en ${countdown} segundo${countdown !== 1 ? 's' : ''}...`
                }
              </Typography>
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} style={{ color: "#155724" }} />
              </div>
            </div>
          )}
          
          <Formik
            initialValues={initialState}
            enableReinitialize={true}
            validationSchema={UserSchema}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSignUp(values);
                actions.setSubmitting(false);
              }, 400);
            }}
          >
            {({ touched, errors, isSubmitting, values, setFieldValue, resetForm }) => (
              <Form style={{ opacity: registrationSuccess ? 0.5 : 1, pointerEvents: registrationSuccess ? 'none' : 'auto' }}>
                <Grid container spacing={2} className={classes.formGrid}>
                  {/* Información Básica */}
                  <Grid item xs={12}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <BusinessIcon />
                      Información de la Empresa
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        name="name"
                        label={i18n.t("companies.form.name")}
                        variant="outlined"
                        fullWidth
                        value={values.name || ""}
                        onChange={(e) => setFieldValue('name', e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <BusinessIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                        required
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        name="email"
                        label={i18n.t("signup.form.email")}
                        variant="outlined"
                        fullWidth
                        type="email"
                        value={values.email || ""}
                        onChange={(e) => setFieldValue('email', e.target.value)}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <EmailIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                        required
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        name="phone"
                        label={i18n.t("companies.form.phone")}
                        variant="outlined"
                        fullWidth
                        value={values.phone || ""}
                        onChange={(e) => setFieldValue('phone', e.target.value)}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        className={classes.textField}
                        inputProps={{ 
                          maxLength: 15,
                          placeholder: "(99) 99999-9999"
                        }}
                        InputProps={{
                          startAdornment: <PhoneIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                        required
                      />
                    </div>
                  </Grid>

                  {/* Configuración de Acceso */}
                  <Grid item xs={12}>
                    <Divider style={{ margin: "20px 0" }} />
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <LockIcon />
                      Configuración de Acceso
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        name="password"
                        label={i18n.t("signup.form.password")}
                        variant="outlined"
                        fullWidth
                        type="password"
                        value={values.password || ""}
                        onChange={(e) => setFieldValue('password', e.target.value)}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <LockIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                        required
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField} error={touched.planId && Boolean(errors.planId)}>
                        <InputLabel>{i18n.t("companies.form.plan")}</InputLabel>
                        <Select
                          name="planId"
                          value={values.planId || ""}
                          onChange={(e) => setFieldValue('planId', e.target.value)}
                          label={i18n.t("companies.form.plan")}
                          required
                        >
                          {plans.map((plan, key) => (
                            <MenuItem key={key} value={plan.id}>
                              <Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <StarIcon style={{ fontSize: 16, color: "#FF9800" }} />
                                  <strong>{plan.name}</strong>
                                </Box>
                                <Typography className={classes.planDescription}>
                                  Atendentes: {plan.users} • WhatsApp: {plan.connections} • Filas: {plan.queues} • ${plan.value}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {touched.planId && errors.planId && (
                        <Typography variant="caption" color="error" style={{ marginTop: 4, display: "block" }}>
                          {errors.planId}
                        </Typography>
                      )}
                    </div>
                  </Grid>
                </Grid>

                {/* Botones de Acción */}
                <div className={classes.buttonContainer}>
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.secondaryButton}`}
                    onClick={onCancel || handleBack}
                    startIcon={<ArrowBackIcon />}
                    disabled={loading}
                  >
                    {onCancel ? "Cancelar" : "Volver"}
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    className={`${classes.actionButton} ${classes.primaryButton}`}
                    disabled={loading || isSubmitting || registrationSuccess}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                  >
                    {loading ? "Registrando..." : registrationSuccess ? "¡Registrado!" : i18n.t("signup.buttons.submit")}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterCompanyImproved;
